export interface VideoRecorderOptions {
  videoElement: HTMLVideoElement;
  outputPath?: string;
  mimeType?: string;
  videoBitsPerSecond?: number;
}

export class VideoRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private videoElement: HTMLVideoElement;
  private stream: MediaStream | null = null;
  private isRecording: boolean = false;
  private outputPath: string;
  private mimeType: string;
  private videoBitsPerSecond: number;

  constructor(options: VideoRecorderOptions) {
    this.videoElement = options.videoElement;
    this.outputPath = options.outputPath || this.generateOutputPath();
    this.mimeType = options.mimeType || this.getSupportedMimeType();
    this.videoBitsPerSecond = options.videoBitsPerSecond || 2500000;
  }

  private generateOutputPath(): string {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    return `scan-recording-${timestamp}.webm`;
  }

  private getSupportedMimeType(): string {
    const types = [
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8",
      "video/webm",
      "video/mp4",
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        console.log(`Video recording: Using MIME type: ${type}`);
        return type;
      }
    }

    console.warn(
      "Video recording: No supported MIME type found, using default"
    );
    return "video/webm";
  }

  private async getMediaStream(): Promise<MediaStream> {
    if (this.videoElement.srcObject instanceof MediaStream) {
      console.log("Video recording: Using stream from srcObject");
      return this.videoElement.srcObject;
    }

    try {
      console.log(
        "Video recording: Attempting captureStream() from video element"
      );
      const stream =
        (this.videoElement as any).captureStream?.() ||
        (this.videoElement as any).mozCaptureStream?.();
      if (stream) {
        return stream;
      }
    } catch (e) {
      console.warn("Video recording: captureStream() failed", e);
    }

    console.log("Video recording: Falling back to canvas capture");
    const canvas = document.createElement("canvas");
    canvas.width = this.videoElement.videoWidth || 640;
    canvas.height = this.videoElement.videoHeight || 480;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Failed to get canvas context for video recording");
    }

    const stream = canvas.captureStream(30);

    const drawFrame = () => {
      if (this.isRecording && this.videoElement.readyState >= 2) {
        ctx.drawImage(this.videoElement, 0, 0, canvas.width, canvas.height);
        requestAnimationFrame(drawFrame);
      }
    };

    drawFrame();
    return stream;
  }

  async startRecording(): Promise<void> {
    if (this.isRecording) {
      console.warn("Video recording: Already recording");
      return;
    }

    try {
      console.log("Video recording: Starting recording...");

      this.stream = await this.getMediaStream();

      if (!this.stream) {
        throw new Error("Failed to get media stream from video element");
      }

      const options: MediaRecorderOptions = {
        mimeType: this.mimeType,
        videoBitsPerSecond: this.videoBitsPerSecond,
      };

      this.mediaRecorder = new MediaRecorder(this.stream, options);
      this.recordedChunks = [];

      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
          // console.log(`Video recording: Chunk received, size: ${event.data.size} bytes`);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.finalizeRecording();
      };

      this.mediaRecorder.onerror = (event: Event) => {
        console.error("Video recording: Error occurred:", event);
      };

      this.mediaRecorder.start(1000);
      this.isRecording = true;

      console.log(
        `Video recording: Recording started successfully with MIME type: ${this.mimeType}`
      );
    } catch (error) {
      console.error("Video recording: Failed to start recording:", error);
      this.isRecording = false;
      throw error;
    }
  }

  async stopRecording(): Promise<Blob | null> {
    if (!this.isRecording || !this.mediaRecorder) {
      console.warn("Video recording: Not recording");
      return null;
    }

    return new Promise((resolve) => {
      if (!this.mediaRecorder) {
        console.warn(
          "Video recording: No mediaRecorder instance in stopRecording"
        );
        resolve(null);
        return;
      }

      this.mediaRecorder.onstop = () => {
        console.log("Video recording: MediaRecorder.onstop event fired");
        const blob = this.finalizeRecording();
        resolve(blob);
      };

      if (this.mediaRecorder.state === "recording") {
        console.log("Video recording: Calling mediaRecorder.stop()");
        this.mediaRecorder.stop();
      } else {
        console.warn(
          `Video recording: mediaRecorder state is ${this.mediaRecorder.state}, not 'recording'`
        );
        const blob = this.finalizeRecording();
        resolve(blob);
      }
      this.isRecording = false;
    });
  }

  private finalizeRecording(): Blob | null {
    if (this.recordedChunks.length === 0) {
      console.warn("Video recording: No chunks recorded");
      return null;
    }

    const blob = new Blob(this.recordedChunks, { type: this.mimeType });
    console.log(
      `Video recording: Recording finalized, size: ${blob.size} bytes, chunks: ${this.recordedChunks.length}`
    );

    this.recordedChunks = [];
    this.mediaRecorder = null;
    this.stream = null;

    return blob;
  }

  downloadVideo(blob: Blob, filename?: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || this.outputPath;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    console.log(`Video recording: Video downloaded as ${a.download}`);
  }

  getRecordingState(): boolean {
    return this.isRecording;
  }

  getOutputPath(): string {
    return this.outputPath;
  }
}
